import { SECTION_CARD } from '@/components/ui/section-card-variants'
import Skeleton from '@/components/ui/Skeleton'
import { cn } from '@/lib/utils'

/**
 * The rating summary beside the reviews: a 320px column for the summary (its
 * own `@xl:w-80`) and the rest for the cards, from `@3xl` (48rem) of the main
 * column, where the cards keep about 480px; one stack below it. Shared with
 * `BrokerReviewsScreen` so the pending page splits where the loaded one does.
 */
export const REVIEWS_COLUMNS =
  'grid gap-6 @3xl:grid-cols-[--spacing(80)_minmax(0,1fr)] @3xl:items-start'

/*
 * Brokers · Reviews before its first response: the LOADED page, block for
 * block — the same rule Market's own `MarketBoardStates.tsx` sets out. Both
 * blocks below are the loaded card's OWN canonical `skeleton` (copied out of
 * `RatingSummary.tsx` and `ReviewList.tsx` — this file cannot import those,
 * route `pendingComponent`s are not code-split), inside `RatingSummary`'s own
 * `SectionCard` chrome; `ReviewList` draws no card of its own.
 *
 *   broker picker          the toolbar's 36px chip (44 below `lg`)
 *   rating summary   366   `SectionCard as="article"`, `w-full @xl:w-80`
 *   review list      374   three review-card-sized bars, `ReviewList`'s own
 *                          typical count (its loaded page can hold up to 20)
 *
 * The summary and the list sit in `REVIEWS_COLUMNS`, side by side from `@3xl`
 * of the main column (V2's 1440 frame) and stacked below it.
 *
 * Bars only, no words, so nothing here can be mistaken for, or found by a
 * test as, the loaded content.
 */

/** `RatingSummary`'s own `skeleton` prop, inside its own `SectionCard` box. */
function RatingSummarySkeleton() {
  return (
    <article className={cn(SECTION_CARD, 'w-full @xl:w-80')}>
      <div className="flex flex-col gap-4">
        <Skeleton className="h-12 w-24 rounded-sm" />
        <Skeleton className="h-4 w-32 rounded-xs" />
        <Skeleton className="h-28 w-full rounded-sm" />
      </div>
    </article>
  )
}

/** `ReviewList`'s own `skeleton` prop — three review-card-sized bars, no card chrome. */
function ReviewListSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      {[0, 1, 2].map((index) => (
        <Skeleton key={index} className="h-28 w-full rounded-lg" />
      ))}
    </div>
  )
}

export function ReviewsSkeleton() {
  return (
    <>
      <Skeleton className="h-11 w-44 rounded-sm lg:h-9" />
      <div className={REVIEWS_COLUMNS}>
        <RatingSummarySkeleton />
        <ReviewListSkeleton />
      </div>
    </>
  )
}
